'use client';

// react
import { useCallback, useEffect, useMemo, useState } from 'react';
// components
import { LoadingScreen } from '@/components/minimal';
// @mui
import { Box, Fab, Typography } from '@mui/material';
// services
import { InvestorsService, UserGroupsService } from '@/services';
// next
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
// utils
import { setUserGroup } from '@/utils/userGroup';
// hooks
import { useAuth, useFetch, useSelectedUserGroup } from '@/hooks';
// sections
const ChatModal = dynamic(() => import('@/sections/common/chat/ChatModal'));
const Error = dynamic(() => import('@/sections/common/Error'));
const PublicQuestions = dynamic(() => import('@/sections/common/PublicQuestions'));
const UserGroupContents = dynamic(() => import('@/sections/common/UserGroupContents'));
const UserGroupRatings = dynamic(() => import('@/sections/common/UserGroupRatings'));
import { AboutUserGroup, UserGroupActions } from '@/sections/common';
// enums
import { USER_GROUP_TYPE, EVENT_TYPE } from '@/enums';
// constants
import { paths } from '@/routes/paths';

type InvestorDetailViewProps = {
  userGroupId: number;
};

export default function InvestorDetailView({ userGroupId }: InvestorDetailViewProps) {
  const { push } = useRouter();
  const pathname = usePathname();
  const { user } = useAuth();

  const selectedUserGroup = useSelectedUserGroup();

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [investorPageDetail, setInvestorPageDetail] = useState<any>({});
  const [investorContents, setInvestorContents] = useState([]);
  const [publicQuestions, setPublicQuestions] = useState({
    investorQuestions: [],
    showApplyToPitchButton: false,
  });

  const isMember = useMemo(
    () =>
      Boolean(
        (
          ((user || {}).userGroupMembers || []).filter(
            (item) =>
              item.userGroup.id === Number(userGroupId) &&
              item.userGroup.userGroupType === USER_GROUP_TYPE.INVESTOR
          ) || []
        ).length > 0
      ),
    [user, userGroupId]
  );

  const userGroupContents = useMemo(
    () =>
      investorContents.filter(({ type }) =>
        [EVENT_TYPE.MEETING, EVENT_TYPE.TRAINING_SESSION, EVENT_TYPE.PITCH_EVENT].includes(type)
      ),
    [investorContents]
  );

  const isStartupMember = useMemo(
    () =>
      Boolean(
        (
          ((user || {}).userGroupMembers || []).filter(
            (item) => item.userGroup.userGroupType === USER_GROUP_TYPE.STARTUP
          ) || []
        ).length > 0
      ),
    [user]
  );

  const fetchData = useCallback(async () => {
    const [
      { success: investorDetailSuccess, ...investorDetailResponse },
      { success: publicQuestionsSuccess, ...publicQuestionsResponse },
      { success: investorContentsSuccess, data: investorContentsData = [] },
    ] = await Promise.all([
      InvestorsService.getDetail(userGroupId),
      InvestorsService.getDetailQuestions(userGroupId),
      UserGroupsService.getContents(userGroupId),
    ]);

    if (investorDetailSuccess) setInvestorPageDetail(investorDetailResponse.data);
    else return false;

    if (publicQuestionsSuccess) setPublicQuestions(publicQuestionsResponse.data);

    if (investorContentsSuccess) setInvestorContents(investorContentsData);

    return true;
  }, [userGroupId]);

  useEffect(() => {
    const hash = pathname?.split('#')[1];
    if (hash) {
      const sectionElement = document.getElementById(hash);
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [pathname]);

  const { loading, notFound } = useFetch(fetchData, [selectedUserGroup?.id]);

  if (notFound) return <Error statusCode={404} />;

  return (
    <>
      {loading && <LoadingScreen />}
      <AboutUserGroup
        userGroupPageDetail={investorPageDetail}
        userGroupType={USER_GROUP_TYPE.INVESTOR}
      />
      {/* {Boolean(publicQuestions?.investorQuestions?.length) && (
        <PublicQuestions
          publicQuestions={publicQuestions.investorQuestions}
          isMember={isMember}
          userGroupType={USER_GROUP_TYPE.INVESTOR}
        />
      )} */}

      {Boolean(userGroupContents?.length) && (
        <UserGroupContents contents={userGroupContents} title="Upcoming Events" />
      )}

      <UserGroupActions
        showUserGroupActionButton={publicQuestions?.investorQuestions?.length}
        userGroupId={userGroupId}
        isStartupMember={isStartupMember}
        setShowMessageModal={setShowMessageModal}
        userGroupType={USER_GROUP_TYPE.INVESTOR}
        submitted={investorPageDetail?.submitted}
        calendlyLink={undefined}
      />
      <div id="ratings">
        <UserGroupRatings
          userGroupId={userGroupId}
          mutate={fetchData}
          ratingActive={investorPageDetail?.userCanRate}
          ratingScore={investorPageDetail?.ratingScore}
          ratingCount={investorPageDetail?.totalRatingCount}
          userGroupType={USER_GROUP_TYPE.INVESTOR}
        />
      </div>
      {isMember && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 140,
            right: 20,
            zIndex: '99',
            border: 'none',
            outline: 'none',
            borderRadius: '4px',
          }}
        >
          <Fab
            onClick={async () => {
              const userGroup = user?.userGroups?.find(({ id }) => id == userGroupId);
              if (!userGroup) return;
              setUserGroup(userGroup);
              push(paths.page_management.investor.builder);
            }}
            variant="extended"
          >
            <Typography color="white" variant="h6">
              Go to builder
            </Typography>
          </Fab>
        </Box>
      )}
      {user && showMessageModal && (
        <ChatModal
          open={showMessageModal}
          setOpen={setShowMessageModal}
          userGroupId={userGroupId}
        />
      )}
    </>
  );
}
